"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as FVF from './formfieldvalidations';
/**
 * Centralized field validation service
 */
export async function validateField(fieldProps, event) {
    const errors = [];
    let isValid = true;
    // 1. Field-specific validation (e.g., isValidEmailAddress, isValidUrl, etc.)
    if (fieldProps.validate && typeof fieldProps.validate === "string" && fieldProps.validate in FVF) {
        const fieldValid = FVF[fieldProps.validate](event.target);
        if (!fieldValid) {
            isValid = false;
            errors.push(`${fieldProps.validate} validation failed`);
        }
    }
    // 2. Parent validation (for grouped/nested fields)
    if (fieldProps.parent?.validate && typeof fieldProps.parent.validate === "string" && fieldProps.parent.validate in FVF) {
        const parentValid = FVF[fieldProps.parent.validate](event.target);
        if (!parentValid) {
            isValid = false;
            errors.push(`Parent ${fieldProps.parent.validate} validation failed`);
        }
    }
    // 3. HTML5 required validation
    if (fieldProps.required) {
        const requiredValid = event.target.checkValidity();
        if (!requiredValid) {
            isValid = false;
            errors.push('Required field validation failed');
        }
    }
    // 4. Required not empty check
    if (fieldProps.required && !event.target.value) {
        isValid = false;
        errors.push('Field cannot be empty');
    }
    // 5. Custom validation rules can be added here
    // Example: minimum length, maximum length, pattern matching, etc.
    return { isValid, errors };
}
/**
 * Form validation context for managing form-wide validation state
 */
const FormValidationContext = createContext(null);
export function useFormValidation() {
    const context = useContext(FormValidationContext);
    if (!context) {
        throw new Error('useFormValidation must be used within FormValidationProvider');
    }
    return context;
}
/**
 * FormValidationProvider — Context provider for form validation state and helpers (field errors, validity checks).
 *
 * @param {node} [props.children] - Children components that require validation context (usually `FormEngine`).
 */
FormValidationProvider.propTypes = {
    /** Child nodes that consume the validation context */
    children: PropTypes.node.isRequired
};
export function FormValidationProvider({ children }) {
    const [fieldValidity, setFieldValidity] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const validateField = useCallback((fieldId, isValid, errors) => {
        setFieldValidity(prev => ({ ...prev, [fieldId]: isValid }));
        setFieldErrors(prev => ({ ...prev, [fieldId]: errors }));
    }, []);
    const validateAllFields = useCallback(() => {
        return Object.values(fieldValidity).every(valid => valid === true);
    }, [fieldValidity]);
    const resetValidation = useCallback(() => {
        setFieldValidity({});
        setFieldErrors({});
    }, []);
    const isFormValid = Object.values(fieldValidity).every(valid => valid === true);
    const value = {
        fieldValidity,
        fieldErrors,
        isFormValid,
        validateField,
        validateAllFields,
        resetValidation
    };
    return (_jsx(FormValidationContext.Provider, { value: value, children: children }));
}
/**
 * Cross-field validation functions
 */
export function validatePasswordMatch(formData) {
    const password = formData.password?.value;
    const confirm = formData.passwordConfirm?.value;
    if (!password || !confirm) {
        return { isValid: true, errors: [] }; // Not required fields
    }
    const isValid = password === confirm;
    return {
        isValid,
        errors: isValid ? [] : ['Passwords do not match']
    };
}
export function validateAgeRestriction(formData) {
    const age = parseInt(formData.age?.value);
    const hasParentalConsent = formData.parentalConsent?.value === 'yes';
    if (isNaN(age) || age >= 13) {
        return { isValid: true, errors: [] };
    }
    const isValid = hasParentalConsent;
    return {
        isValid,
        errors: isValid ? [] : ['Parental consent required for users under 13']
    };
}
/**
 * Form-level validation orchestrator
 */
export function validateFormLevel(formData, customValidators = []) {
    const allErrors = [];
    let isValid = true;
    // Run custom cross-field validations
    const defaultValidators = [validatePasswordMatch, validateAgeRestriction];
    const allValidators = [...defaultValidators, ...customValidators];
    for (const validator of allValidators) {
        const result = validator(formData);
        if (!result.isValid) {
            isValid = false;
            allErrors.push(...result.errors);
        }
    }
    return { isValid, errors: allErrors };
}
